import { Icon } from "@chakra-ui/react";
import { EmrRos } from "@medstonetech/slate-icons";
import { ProviderChartCode } from "enums";
import {
  ChartRow,
  ChartRowElement,
  ChartRowElementImportedProps,
  ChartRowGroup,
  ChartSectionHeader,
  ChartSubsection,
  ChartSubsectionContent,
  ChartSubsectionHeader,
  useChartForm,
} from "modules";
import * as React from "react";
import { FormProvider } from "react-hook-form";
import { Loading } from "shared";
import { usePermissions } from "contexts";
import { ChartForm } from "modules/charts-shared-v2";

const CHART_CODE: ProviderChartCode = "C006x";
const SECTION_CODE = "SROS";

function T06ROS() {
  const {
    getIsLoading,
    encounterId,
    onSubmit,
    updateIsLoading,
    useFormContext,
    isDirty,
    onRouteChangeSave,
    onSaveAndNext,
  } = useChartForm({
    chartCode: CHART_CODE,
    sectionId: SECTION_CODE,
    sectionLabel: "ROS",
  });
  const { scope } = usePermissions();
  const isReadOnly = !scope("provider:ros").isEditable;

  if (getIsLoading) {
    return <Loading />;
  }

  const { getValues } = useFormContext;

  const chartFavoriteEditMode: Record<
    "imported",
    ChartRowElementImportedProps
  > | null = getValues("chartFavoriteEditMode")
    ? {
        imported: {
          type: "nursing",
        },
      }
    : null;

  return (
    <FormProvider {...useFormContext}>
      <ChartForm
        onSubmit={onSubmit}
        onRouteChangeSave={onRouteChangeSave}
        isDirty={isDirty}
        isPromptSaveLoading={updateIsLoading}
        disabled={isReadOnly}
        sectionId={SECTION_CODE}
      >
        <ChartSectionHeader
          chartId={CHART_CODE}
          encounterId={encounterId}
          sectionId={SECTION_CODE}
          icon={<Icon as={EmrRos} />}
          showActionsBtns
          isLoading={updateIsLoading}
          onSaveAndNext={onSaveAndNext}
          showFavoritesBtn={!isReadOnly}
        >
          ROS {isReadOnly ? "(Read Only)" : ""}
        </ChartSectionHeader>

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={SECTION_CODE}
              subsectionId="Q001"
              subsectionLabel="MS"
            >
              MS
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowGroup width="300px">
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="normal"
                    name="Q001.C003xSROSQ001A001"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="neck"
                    name="Q001.C006xSROSQ001A004"
                  />
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="back pain"
                    name="Q001.C003xSROSQ001A002"
                  />
                  <ChartRowElement
                    type="input"
                    {...chartFavoriteEditMode}
                    name="Q001.C003xSROSQ001A003"
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={SECTION_CODE}
              subsectionId="Q002"
              subsectionLabel="CONST"
            >
              CONST
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowGroup width="300px">
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="normal"
                    name="Q002.C003xSROSQ002A001"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="recent illness"
                    name="Q002.C003xSROSQ002A002"
                  />
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="fever"
                    name="Q002.C003xSROSQ002A003"
                  />
                  <ChartRowElement
                    type="input"
                    {...chartFavoriteEditMode}
                    name="Q002.C003xSROSQ002A004"
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={SECTION_CODE}
              subsectionId="Q003"
              subsectionLabel="CVS"
            >
              CVS
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowGroup width="300px">
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="normal"
                    name="Q003.C003xSROSQ003A001"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="chest pain"
                    name="Q003.C003xSROSQ003A002"
                  />
                  <ChartRowElement
                    type="input"
                    {...chartFavoriteEditMode}
                    name="Q003.C003xSROSQ003A003"
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={SECTION_CODE}
              subsectionId="Q004"
              subsectionLabel="RESP"
            >
              RESP
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowGroup width="300px">
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="normal"
                    name="Q004.C003xSROSQ004A001"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="shortness of breath"
                    name="Q004.C003xSROSQ004A002"
                  />
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="cough"
                    name="Q004.C006xSROSQ004A004"
                  />
                  <ChartRowElement
                    type="input"
                    {...chartFavoriteEditMode}
                    name="Q004.C003xSROSQ004A003"
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={SECTION_CODE}
              subsectionId="Q005"
              subsectionLabel="GU"
            >
              GU
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowGroup width="300px">
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="normal"
                    name="Q005.C003xSROSQ005A001"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="problems urinating"
                    name="Q005.C003xSROSQ005A002"
                  />
                  <ChartRowElement
                    type="input"
                    {...chartFavoriteEditMode}
                    name="Q005.C003xSROSQ005A003"
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={SECTION_CODE}
              subsectionId="Q006"
              subsectionLabel="LNMP"
            >
              LNMP
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowGroup width="300px">
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="normal"
                    name="Q006.C003xSROSQ006A001"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="preg"
                    name="Q006.C003xSROSQ006A002"
                  />
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="post-menop"
                    name="Q006.C003xSROSQ006A003"
                  />
                  <ChartRowElement
                    type="input"
                    {...chartFavoriteEditMode}
                    name="Q006.C003xSROSQ006A004"
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={SECTION_CODE}
              subsectionId="Q007"
              subsectionLabel="GI"
            >
              GI
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowGroup width="300px">
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="normal"
                    name="Q007.C003xSROSQ007A001"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="nausea"
                    name="Q007.C003xSROSQ007A002"
                  />
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="vomiting"
                    name="Q007.C003xSROSQ007A003"
                  />
                  <ChartRowElement
                    type="input"
                    {...chartFavoriteEditMode}
                    name="Q007.C003xSROSQ007A004"
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={SECTION_CODE}
              subsectionId="Q008"
              subsectionLabel="NEURO"
            >
              NEURO
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowGroup width="300px">
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="normal"
                    name="Q008.C003xSROSQ008A001"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="headache"
                    name="Q008.C003xSROSQ008A002"
                  />
                  <ChartRowElement
                    type="input"
                    {...chartFavoriteEditMode}
                    name="Q008.C003xSROSQ008A004"
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={SECTION_CODE}
              subsectionId="Q009"
              subsectionLabel="LYMPH"
            >
              LYMPH
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowGroup width="300px">
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="normal"
                    name="Q009.C003xSROSQ009A001"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="ankle swelling"
                    name="Q009.C003xSROSQ009A002"
                  />
                  <ChartRowElement
                    type="circle-option"
                    label="L"
                    name="Q009.C003xSROSQ009A003"
                  />
                  <ChartRowElement
                    type="circle-option"
                    label="R"
                    name="Q009.C003xSROSQ009A004"
                  />
                  <ChartRowElement
                    type="input"
                    {...chartFavoriteEditMode}
                    name="Q009.C003xSROSQ009A005"
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={SECTION_CODE}
              subsectionId="Q010"
              subsectionLabel="SKIN"
            >
              SKIN
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowGroup width="300px">
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="normal"
                    name="Q010.C003xSROSQ010A001"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="rash"
                    name="Q010.C003xSROSQ010A002"
                  />
                  <ChartRowElement
                    type="input"
                    {...chartFavoriteEditMode}
                    name="Q010.C003xSROSQ010A003"
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={SECTION_CODE}
              subsectionId="Q011"
              subsectionLabel="PSYCH"
            >
              PSYCH
            </ChartSubsectionHeader>
          }
          content={
            <>
              <ChartSubsectionContent>
                <ChartRow>
                  <ChartRowGroup width="300px">
                    <ChartRowElement
                      type="cross-option"
                      {...chartFavoriteEditMode}
                      label="normal"
                      name="Q011.C003xSROSQ011A001"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="cross-option"
                      {...chartFavoriteEditMode}
                      label="anxiety"
                      name="Q011.C003xSROSQ011A002"
                    />
                    <ChartRowElement
                      type="cross-option"
                      {...chartFavoriteEditMode}
                      label="depression"
                      name="Q011.C003xSROSQ011A003"
                    />
                    <ChartRowElement
                      type="input"
                      {...chartFavoriteEditMode}
                      name="Q011.C003xSROSQ011A004"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>

              <ChartSubsectionContent>
                <ChartRow>
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    label="except as marked positive, all systems above reviewed and found negative"
                    name="Q011.C003xSROSQ011A005"
                  />
                  <ChartRowElement type="empty" />
                </ChartRow>
              </ChartSubsectionContent>

              <ChartSubsectionContent>
                <ChartRow>
                  <ChartRowElement
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name="Q011.C003xSROSQ011A006"
                    label="NEURO / MS components also addressed in HPI"
                  />
                  <ChartRowElement type="empty" />
                </ChartRow>
              </ChartSubsectionContent>
            </>
          }
        />
      </ChartForm>
    </FormProvider>
  );
}

export { T06ROS };
